import { getProblems, problemColors } from "../../shared/helper";
import { 
    Box, 
    Typography, 
    Chip,
    Paper,
    alpha
} from '@mui/material';
import { useSelector } from 'react-redux';

// Иконки
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SensorsIcon from '@mui/icons-material/Sensors';
import NotesIcon from '@mui/icons-material/Notes';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const ComplexChilds = props => {
    const { host, hostType = 'default' } = props;
    const isDarkMode = useSelector(state => state.settings.darkMode);
    const childs = host.child;

    if (hostType !== 'crossroad' || !childs) {
        return (
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 2, md: 4 },
                    textAlign: 'center',
                    backgroundColor: 'var(--background-paper)',
                    borderRadius: { xs: '8px', md: '12px' },
                    border: '1px solid var(--border-default)'
                }}
            >
                <SensorsIcon sx={{ fontSize: { xs: 40, md: 56 }, color: 'var(--text-muted)', mb: { xs: 1.5, md: 2 }, opacity: 0.5 }} />
                <Typography sx={{ fontSize: { xs: '0.875rem', md: '1rem' }, fontWeight: 500, color: 'var(--text-primary)', mb: 0.5 }}>
                    Дочерние элементы недоступны
                </Typography>
                <Typography sx={{ fontSize: { xs: '0.75rem', md: '0.85rem' }, color: 'var(--text-muted)' }}>
                    Этот раздел доступен только для перекрестков
                </Typography>
            </Paper>
        );
    }

    const getMaxPriority = (problems) => {
        if (!problems || problems.length === 0) return 0;
        return Math.max(...problems.map(p => getPriorityByColor(p[4])));
    };

    const getPriorityByColor = (color) => {
        const map = { '#541FB3': 0, '#28a1d7': 1, '#f5bb3a': 2, '#ef9227': 3, '#d63a23': 4, '#a23337': 5 };
        return map[color] || 0;
    };

    const renderChildCard = (childHost) => {
        const note = childHost.items.find(item => item.key_ === 'notes');
        const problems = getProblems(childHost);
        const hasProblems = problems.length > 0;
        const childName = childHost.name.split('/')[1];
        
        const maxPriority = getMaxPriority(problems);
        const mainColor = hasProblems ? problemColors[maxPriority] : null;

        return (
            <Paper 
                key={childName}
                elevation={0}
                sx={{ 
                    borderRadius: { xs: '8px', md: '12px' },
                    backgroundColor: 'var(--background-paper)',
                    border: `1px solid ${hasProblems ? alpha(mainColor, 0.4) : 'var(--border-default)'}`,
                    overflow: 'hidden',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                        boxShadow: isDarkMode ? '0 4px 16px rgba(0,0,0,0.3)' : '0 4px 16px rgba(0,0,0,0.1)',
                    }
                }}
            >
                {/* Заголовок - компактный */}
                <Box sx={{
                    p: { xs: 1, md: 1.25 },
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 0.75, md: 1 },
                    backgroundColor: hasProblems ? alpha(mainColor, 0.06) : 'var(--background-subtle)',
                    borderBottom: `1px solid ${hasProblems ? alpha(mainColor, 0.2) : 'var(--border-default)'}`
                }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: { xs: 24, md: 28 },
                        height: { xs: 24, md: 28 },
                        borderRadius: { xs: '4px', md: '6px' },
                        backgroundColor: hasProblems ? mainColor : 'var(--success-color)',
                        color: 'white',
                        flexShrink: 0
                    }}>
                        {hasProblems ? <ErrorIcon sx={{ fontSize: { xs: 14, md: 16 } }} /> : <CheckCircleIcon sx={{ fontSize: { xs: 14, md: 16 } }} />}
                    </Box>
                    
                    <Typography sx={{ 
                        fontSize: { xs: '0.75rem', md: '0.85rem' }, 
                        fontWeight: 600,
                        color: 'var(--text-primary)',
                        flex: 1,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}>
                        {childName}
                    </Typography>

                    {hasProblems && (
                        <Chip
                            label={`${problems.length} проблем`}
                            size="small"
                            sx={{
                                height: { xs: 20, md: 22 },
                                fontSize: { xs: '0.65rem', md: '0.7rem' },
                                fontWeight: 600,
                                backgroundColor: mainColor,
                                color: 'white',
                                flexShrink: 0
                            }}
                        />
                    )}
                </Box>

                {/* Заметка - если есть */}
                {note && note.lastvalue !== '' && (
                    <Box sx={{ 
                        p: { xs: 0.75, md: 1 },
                        m: { xs: 0.75, md: 1 },
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: { xs: 0.5, md: 0.75 },
                        backgroundColor: alpha('#f5bb3a', 0.08),
                        border: `1px solid ${alpha('#f5bb3a', 0.25)}`,
                        borderRadius: { xs: '6px', md: '8px' },
                    }}>
                        <NotesIcon sx={{ fontSize: { xs: 12, md: 14 }, color: '#f5bb3a', mt: 0.25, flexShrink: 0 }} />
                        <Typography sx={{ 
                            color: 'var(--text-primary)',
                            fontSize: { xs: '0.7rem', md: '0.75rem' },
                            lineHeight: 1.4,
                        }}>
                            {note.lastvalue}
                        </Typography>
                    </Box>
                )}

                {/* Содержимое */}
                <Box sx={{ p: { xs: 0.75, md: 1 } }}>
                    {!hasProblems ? (
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            gap: { xs: 0.5, md: 0.75 },
                            py: { xs: 0.75, md: 1 }
                        }}>
                            <CheckCircleIcon sx={{ fontSize: { xs: 14, md: 16 }, color: 'var(--success-color)' }} />
                            <Typography sx={{ 
                                fontSize: { xs: '0.75rem', md: '0.8rem' }, 
                                color: 'var(--success-color)',
                                fontWeight: 500
                            }}>
                                Работа в штатном режиме
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0.4, md: 0.5 } }}>
                            {problems.map((problem, index) => {
                                const pColor = problem[4];
                                const problemName = problem[2];
                                const problemValue = problem[3];
                                
                                return (
                                    <Box
                                        key={index}
                                        sx={{
                                            p: { xs: 0.5, md: 0.75 },
                                            pl: { xs: 1, md: 1.25 },
                                            backgroundColor: alpha(pColor, 0.06),
                                            borderLeft: { xs: `2px solid ${pColor}`, md: `3px solid ${pColor}` },
                                            borderRadius: { xs: '0 4px 4px 0', md: '0 6px 6px 0' },
                                            transition: 'all 0.15s ease',
                                            '&:hover': {
                                                backgroundColor: alpha(pColor, 0.1),
                                            }
                                        }}
                                    >
                                        <Typography 
                                            sx={{ 
                                                fontWeight: 500,
                                                color: pColor,
                                                fontSize: { xs: '0.7rem', md: '0.78rem' },
                                                lineHeight: 1.3,
                                            }}
                                        >
                                            {problemName}
                                        </Typography>
                                        {problemValue && (
                                            <Typography 
                                                sx={{ 
                                                    color: 'var(--text-muted)',
                                                    fontSize: { xs: '0.65rem', md: '0.7rem' },
                                                    lineHeight: 1.3,
                                                    mt: 0.25
                                                }}
                                            >
                                                {problemValue}
                                            </Typography>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            </Paper>
        );
    };

    // Статистика по всем дочерним элементам
    const totalProblems = childs.reduce((acc, child) => acc + getProblems(child).length, 0);
    const problemChilds = childs.filter(child => getProblems(child).length > 0).length;
    const okChilds = childs.length - problemChilds;

    return (
        <Box sx={{ 
            p: { xs: 1, md: 1.5 }, 
            width: '100%', 
            height: '100%', 
            overflow: 'auto',
            backgroundColor: 'var(--background-default)',
            '&::-webkit-scrollbar': { width: { xs: '4px', md: '6px' } },
            '&::-webkit-scrollbar-track': { background: 'var(--background-subtle)', borderRadius: '3px' },
            '&::-webkit-scrollbar-thumb': { 
                background: 'var(--border-default)', 
                borderRadius: '3px',
                '&:hover': { background: 'var(--text-muted)' }
            }
        }}>
            {/* Общая статистика */}
            <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: { xs: 0.75, md: 1 }, 
                mb: { xs: 1, md: 1.5 },
                p: { xs: 0.75, md: 1 },
                backgroundColor: 'var(--background-paper)',
                borderRadius: { xs: '6px', md: '8px' },
                border: '1px solid var(--border-default)'
            }}>
                <SensorsIcon sx={{ fontSize: { xs: 16, md: 18 }, color: 'var(--text-muted)' }} />
                <Typography sx={{ fontSize: { xs: '0.75rem', md: '0.8rem' }, color: 'var(--text-secondary)', flex: 1 }}>
                    Дочерних элементов: <b>{childs.length}</b>
                </Typography>
                {okChilds > 0 && (
                    <Chip
                        icon={<CheckCircleIcon sx={{ fontSize: { xs: 12, md: 14 } }} />}
                        label={okChilds}
                        size="small"
                        sx={{
                            height: { xs: 20, md: 24 },
                            fontSize: { xs: '0.7rem', md: '0.75rem' },
                            fontWeight: 600,
                            backgroundColor: alpha(isDarkMode ? '#3fb950' : '#2da44e', 0.1),
                            color: 'var(--success-color)',
                            border: `1px solid ${alpha(isDarkMode ? '#3fb950' : '#2da44e', 0.3)}`,
                            '& .MuiChip-icon': { color: 'var(--success-color)' }
                        }}
                    />
                )}
                {problemChilds > 0 && (
                    <Chip
                        icon={<WarningAmberIcon sx={{ fontSize: { xs: 12, md: 14 } }} />}
                        label={`${problemChilds} (${totalProblems})`}
                        size="small"
                        sx={{
                            height: { xs: 20, md: 24 },
                            fontSize: { xs: '0.7rem', md: '0.75rem' },
                            fontWeight: 600,
                            backgroundColor: alpha('#d63a23', 0.1),
                            color: '#d63a23',
                            border: `1px solid ${alpha('#d63a23', 0.3)}`,
                            '& .MuiChip-icon': { color: '#d63a23' }
                        }}
                    />
                )}
            </Box>

            {/* Сетка карточек */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
                gap: { xs: 1, md: 1.5 },
                alignItems: 'start'
            }}>
                {[...childs]
                    .sort((a, b) => {
                        // Сначала с проблемами, потом без
                        const aProblems = getProblems(a).length;
                        const bProblems = getProblems(b).length;
                        if (aProblems > 0 && bProblems === 0) return -1;
                        if (aProblems === 0 && bProblems > 0) return 1;
                        // Затем по имени
                        return a.name.split(' / ')[1]?.localeCompare(b.name.split(' / ')[1]);
                    })
                    .map(childHost => renderChildCard(childHost))
                }
            </Box>
            
            {childs.length === 0 && (
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 2, md: 4 },
                        textAlign: 'center',
                        backgroundColor: 'var(--background-paper)',
                        borderRadius: { xs: '8px', md: '12px' },
                        border: '1px solid var(--border-default)'
                    }}
                >
                    <SensorsIcon sx={{ fontSize: { xs: 40, md: 56 }, color: 'var(--text-muted)', mb: { xs: 1.5, md: 2 }, opacity: 0.5 }} />
                    <Typography sx={{ fontSize: { xs: '0.875rem', md: '1rem' }, fontWeight: 500, color: 'var(--text-primary)', mb: 0.5 }}>
                        Дочерние элементы не найдены
                    </Typography>
                    <Typography sx={{ fontSize: { xs: '0.75rem', md: '0.85rem' }, color: 'var(--text-muted)' }}>
                        У данного перекрестка отсутствуют дочерние элементы
                    </Typography>
                </Paper>
            )}
        </Box>
    );
};

export default ComplexChilds;
